import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { useAuth } from '../../shared/auth/AuthContext';
import { useCompanies } from '../organization/useOrganization';

/* ═══════════════════════════════════════════════════════════════════════════
   OKRs — hooks e tipos do módulo (features/okrs)
   ───────────────────────────────────────────────────────────────────────────
   Contratos consumidos (backend já implementado, DTOs reais em
   `backend/apps/api/src/features/okrs/presentation/http/dto/okrs.dto.ts`):

   - GET  /okrs/dashboard          → OkrDashboardDto
          { okrs: OkrDashboardItemDto[], atividadesRecentes: OkrActivityDto[],
            resumo: OkrDashboardResumoDto }
   - GET  /okrs                    → PaginatedOkrResponseDto
          { data: OkrDto[], total, page, limit, totalPages }
   - POST /okrs                    → OkrDto            (CreateOkrDto)
   - PATCH /okrs/:id               → OkrDto            (UpdateOkrDto)
   - DELETE /okrs/:id              → 204               (apenas DRAFT)
   - POST /okrs/:id/activate | complete | archive → OkrDto
   - POST /okrs/:id/key-results    → KeyResultDto      (CreateKeyResultDto)
   - PATCH /okrs/:id/key-results/:krId/progress → KeyResultDto
                                                       (UpdateKeyResultProgressDto)
   - GET  /okrs/goals              → PaginatedDepartmentalGoalResponseDto
          { data: DepartmentalGoalDto[], total, page, limit, totalPages, pesoTotal }
   - POST /okrs/goals, PATCH/DELETE /okrs/goals/:id → DepartmentalGoalDto
   - GET  /okrs/productivity/analysis    → ProductivityAnalysisDto
          { gargalo, oportunidade, scores, departamentos, periodoAtual }
   - GET  /okrs/productivity/comparative → ProductivityComparativeDto
          { departamentos, melhorDepartamento, piorDepartamento, mediaGeral, periodoAtual }
   - GET  /okrs/productivity       → PaginatedMetricsEntryResponseDto

   Tenancy: tenantId + companyId vêm da sessão (padrão do módulo contábil:
   `useAccountingCtx`); nunca do corpo da request.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Tipos (espelham os DTOs reais) ── */

export type OkrStatus = 'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'ARCHIVED';
export type KeyResultStatus = 'NOT_STARTED' | 'ON_TRACK' | 'COMPLETED';
export type GoalPeriodicity = 'MENSAL' | 'TRIMESTRAL' | 'ANUAL';
export type ProductivityTrend = 'SUBINDO' | 'ESTAVEL' | 'CAINDO';

export interface OkrActivity {
  at: string;
  actorId: string;
  actorName: string;
  action: string;
  detail: string;
}

export interface KeyResult {
  id: string;
  okrId: string;
  titulo: string;
  descricao: string | null;
  inicial: number;
  atual: number;
  meta: number;
  progresso: number;
  status: KeyResultStatus;
  createdAt: string;
  updatedAt: string;
}

export interface Okr {
  id: string;
  tenantId: string;
  companyId: string;
  branchId: string | null;
  periodo: string;
  titulo: string;
  descricao: string | null;
  objetivo: string;
  status: OkrStatus;
  keyResults: KeyResult[];
  activities: OkrActivity[];
  progresso: number;
  createdAt: string;
  updatedAt: string;
}

export interface OkrDashboardItem {
  id: string;
  periodo: string;
  titulo: string;
  objetivo: string;
  status: OkrStatus;
  progresso: number;
  keyResultsCount: number;
  keyResultsConcluidos: number;
  updatedAt: string;
}

export interface OkrDashboardResumo {
  total: number;
  ativos: number;
  concluidos: number;
  arquivados: number;
  progressoMedio: number;
  metasDepartamentais: number;
  pesoTotalMetas: number;
}

export interface OkrDashboard {
  okrs: OkrDashboardItem[];
  atividadesRecentes: OkrActivity[];
  resumo: OkrDashboardResumo;
}

export interface OkrInput {
  periodo: string;
  titulo: string;
  descricao?: string;
  objetivo: string;
}

export interface KeyResultInput {
  titulo: string;
  descricao?: string;
  inicial?: number;
  atual?: number;
  meta: number;
}

export interface DepartmentalGoal {
  id: string;
  departamento: string;
  kpi: string;
  periodicidade: GoalPeriodicity;
  owner: string;
  target: number;
  peso: number;
  createdAt: string;
  updatedAt: string;
}

export interface DepartmentalGoalInput {
  departamento: string;
  kpi: string;
  periodicidade?: GoalPeriodicity;
  owner: string;
  target: number;
  peso?: number;
}

export interface GargaloIdentificado {
  departamento: string;
  scoreMedio: number;
  operadores: number;
  piorOperador: string;
}

export interface OportunidadeIdentificada {
  departamento: string;
  scoreMedio: number;
  metaScore: number | null;
  gap: number;
  potencialGanho: number;
}

export interface ScoreOperador {
  departamento: string;
  operadorCelula: string;
  score: number;
  tendencia: ProductivityTrend;
  metaScore: number | null;
}

export interface ScoreDepartamento {
  departamento: string;
  scoreMedio: number;
  melhorOperador: string | null;
  piorOperador: string | null;
  tendencia: ProductivityTrend;
  variacao: number;
  totalOperadores: number;
}

export interface ProductivityAnalysis {
  gargalo: GargaloIdentificado | null;
  oportunidade: OportunidadeIdentificada | null;
  scores: ScoreOperador[];
  departamentos: ScoreDepartamento[];
  periodoAtual: string | null;
}

export interface ProductivityComparative {
  departamentos: ScoreDepartamento[];
  melhorDepartamento: string | null;
  piorDepartamento: string | null;
  mediaGeral: number;
  periodoAtual: string | null;
}

export interface MetricsEntry {
  id: string;
  departamento: string;
  operadorCelula: string;
  score: number;
  tendencia: ProductivityTrend;
  periodo: string;
  metaScore: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface DepartmentalGoalList {
  data: DepartmentalGoal[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  pesoTotal: number;
}

/* ── Contexto (tenant + primeira empresa da sessão) ── */

export interface ModuleCtx {
  tenantId: string;
  companyId: string;
}

export function useModuleCtx(): ModuleCtx | null {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const { data: companies } = useCompanies(tenantId);
  const companyId = companies?.[0]?.id ?? null;
  if (!tenantId || !companyId) return null;
  return { tenantId, companyId };
}

function ctxParams(ctx: ModuleCtx): Record<string, string> {
  return { tenantId: ctx.tenantId, companyId: ctx.companyId };
}

/* ── Labels / variantes de apresentação ── */

export const OKR_STATUS_LABEL: Record<OkrStatus, string> = {
  DRAFT: 'Rascunho',
  ACTIVE: 'Ativo',
  COMPLETED: 'Concluído',
  ARCHIVED: 'Arquivado',
};

export function okrStatusBadgeVariant(
  status: OkrStatus,
): 'info' | 'success' | 'warning' | 'error' | 'neutral' {
  const map: Record<OkrStatus, 'info' | 'success' | 'warning' | 'error' | 'neutral'> = {
    DRAFT: 'neutral',
    ACTIVE: 'info',
    COMPLETED: 'success',
    ARCHIVED: 'error',
  };
  return map[status];
}

export const KEY_RESULT_STATUS_LABEL: Record<KeyResultStatus, string> = {
  NOT_STARTED: 'Não iniciado',
  ON_TRACK: 'Em andamento',
  COMPLETED: 'Concluído',
};

export const GOAL_PERIODICITY_LABEL: Record<GoalPeriodicity, string> = {
  MENSAL: 'Mensal',
  TRIMESTRAL: 'Trimestral',
  ANUAL: 'Anual',
};

export const TREND_LABEL: Record<ProductivityTrend, string> = {
  SUBINDO: 'Subindo',
  ESTAVEL: 'Estável',
  CAINDO: 'Caindo',
};

/* ── Hooks: Dashboard + CRUD /okrs ── */

export function useOkrDashboard(ctx?: ModuleCtx | null) {
  return useQuery({
    queryKey: ['okrs', 'dashboard', ctx] as const,
    queryFn: () =>
      apiClient.get<OkrDashboard>('/okrs/dashboard', {
        params: ctx ? ctxParams(ctx) : {},
      }),
    enabled: !!ctx,
  });
}

export function useOkrs(
  ctx?: ModuleCtx | null,
  params?: { page?: number; limit?: number; periodo?: string; status?: OkrStatus },
) {
  return useQuery({
    queryKey: ['okrs', 'list', ctx, params] as const,
    queryFn: async (): Promise<PaginatedResult<Okr>> => {
      const res = await apiClient.get<PaginatedResult<Okr>>('/okrs', {
        params: {
          ...(ctx ? ctxParams(ctx) : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 20),
          ...(params?.periodo ? { periodo: params.periodo } : {}),
          ...(params?.status ? { status: params.status } : {}),
        },
      });
      return {
        data: res.data ?? [],
        total: res.total ?? 0,
        page: res.page ?? params?.page ?? 1,
        limit: res.limit ?? params?.limit ?? 20,
        totalPages: res.totalPages ?? 1,
      };
    },
    enabled: !!ctx,
  });
}

export function useCreateOkr(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OkrInput) =>
      apiClient.post<Okr>('/okrs', {
        periodo: input.periodo,
        titulo: input.titulo,
        descricao: input.descricao ?? null,
        objetivo: input.objetivo,
      }, { params: ctx ? ctxParams(ctx) : {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['okrs'] }).catch(() => {});
    },
  });
}

export function useUpdateOkr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: OkrInput & { id: string }) =>
      apiClient.patch<Okr>(`/okrs/${id}`, {
        periodo: input.periodo,
        titulo: input.titulo,
        descricao: input.descricao ?? null,
        objetivo: input.objetivo,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['okrs'] }).catch(() => {});
    },
  });
}

export function useDeleteOkr(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete<void>(`/okrs/${id}`, {
        params: ctx ? ctxParams(ctx) : {},
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['okrs'] }).catch(() => {});
    },
  });
}

/** Transições de estado: POST /okrs/:id/activate|complete|archive */
export function useOkrTransition(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, transition }: { id: string; transition: 'activate' | 'complete' | 'archive' }) =>
      apiClient.post<Okr>(`/okrs/${id}/${transition}`, undefined, {
        params: ctx ? ctxParams(ctx) : {},
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['okrs'] }).catch(() => {});
    },
  });
}

export function useAddKeyResult(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ okrId, ...input }: KeyResultInput & { okrId: string }) =>
      apiClient.post<KeyResult>(
        `/okrs/${okrId}/key-results`,
        {
          titulo: input.titulo,
          descricao: input.descricao ?? null,
          inicial: input.inicial ?? 0,
          atual: input.atual ?? input.inicial ?? 0,
          meta: input.meta,
        },
        { params: ctx ? ctxParams(ctx) : {} },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['okrs'] }).catch(() => {});
    },
  });
}

export function useUpdateKeyResultProgress(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      okrId,
      krId,
      ...input
    }: { okrId: string; krId: string; atual?: number; percentual?: number }) =>
      apiClient.patch<KeyResult>(
        `/okrs/${okrId}/key-results/${krId}/progress`,
        { atual: input.atual, percentual: input.percentual },
        { params: ctx ? ctxParams(ctx) : {} },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['okrs'] }).catch(() => {});
    },
  });
}

/* ── Hooks: Metas departamentais (/okrs/goals) ── */

export function useDepartmentalGoals(
  ctx?: ModuleCtx | null,
  params?: { page?: number; limit?: number; departamento?: string; periodicidade?: GoalPeriodicity },
) {
  return useQuery({
    queryKey: ['okrs', 'goals', ctx, params] as const,
    queryFn: async (): Promise<DepartmentalGoalList> => {
      const res = await apiClient.get<DepartmentalGoalList>('/okrs/goals', {
        params: {
          ...(ctx ? ctxParams(ctx) : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 20),
          ...(params?.departamento ? { departamento: params.departamento } : {}),
          ...(params?.periodicidade ? { periodicidade: params.periodicidade } : {}),
        },
      });
      return {
        data: res.data ?? [],
        total: res.total ?? 0,
        page: res.page ?? params?.page ?? 1,
        limit: res.limit ?? params?.limit ?? 20,
        totalPages: res.totalPages ?? 1,
        pesoTotal: res.pesoTotal ?? 0,
      };
    },
    enabled: !!ctx,
  });
}

export function useCreateDepartmentalGoal(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DepartmentalGoalInput) =>
      apiClient.post<DepartmentalGoal>(
        '/okrs/goals',
        {
          departamento: input.departamento,
          kpi: input.kpi,
          periodicidade: input.periodicidade ?? 'TRIMESTRAL',
          owner: input.owner,
          target: input.target,
          peso: input.peso ?? 0,
        },
        { params: ctx ? ctxParams(ctx) : {} },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['okrs', 'goals'] }).catch(() => {});
      qc.invalidateQueries({ queryKey: ['okrs', 'dashboard'] }).catch(() => {});
    },
  });
}

export function useUpdateDepartmentalGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: DepartmentalGoalInput & { id: string }) =>
      apiClient.patch<DepartmentalGoal>(`/okrs/goals/${id}`, {
        departamento: input.departamento,
        kpi: input.kpi,
        periodicidade: input.periodicidade,
        owner: input.owner,
        target: input.target,
        peso: input.peso,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['okrs', 'goals'] }).catch(() => {});
    },
  });
}

export function useDeleteDepartmentalGoal(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete<void>(`/okrs/goals/${id}`, {
        params: ctx ? ctxParams(ctx) : {},
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['okrs', 'goals'] }).catch(() => {});
      qc.invalidateQueries({ queryKey: ['okrs', 'dashboard'] }).catch(() => {});
    },
  });
}

/* ── Hooks: Produtividade (/okrs/productivity) ── */

export function useProductivityAnalysis(
  ctx?: ModuleCtx | null,
  params?: { departamento?: string; periodo?: string },
) {
  return useQuery({
    queryKey: ['okrs', 'productivity', 'analysis', ctx, params] as const,
    queryFn: () =>
      apiClient.get<ProductivityAnalysis>('/okrs/productivity/analysis', {
        params: {
          ...(ctx ? ctxParams(ctx) : {}),
          ...(params?.departamento ? { departamento: params.departamento } : {}),
          ...(params?.periodo ? { periodo: params.periodo } : {}),
        },
      }),
    enabled: !!ctx,
  });
}

export function useProductivityComparative(
  ctx?: ModuleCtx | null,
  params?: { departamento?: string; periodo?: string },
) {
  return useQuery({
    queryKey: ['okrs', 'productivity', 'comparative', ctx, params] as const,
    queryFn: () =>
      apiClient.get<ProductivityComparative>('/okrs/productivity/comparative', {
        params: {
          ...(ctx ? ctxParams(ctx) : {}),
          ...(params?.departamento ? { departamento: params.departamento } : {}),
          ...(params?.periodo ? { periodo: params.periodo } : {}),
        },
      }),
    enabled: !!ctx,
  });
}

export function useMetricsEntries(ctx?: ModuleCtx | null) {
  return useQuery({
    queryKey: ['okrs', 'productivity', 'entries', ctx] as const,
    queryFn: () =>
      apiClient.get<PaginatedResult<MetricsEntry>>('/okrs/productivity', {
        params: ctx ? { ...ctxParams(ctx), limit: '500' } : { limit: '500' },
      }),
    enabled: !!ctx,
  });
}